import { useState } from "react";

import purify from "dompurify";

import { Button, Modal } from "@similarweb/ui-components";
import { Flex, Stack, Text } from "@similarweb/ui-components/foundations";

import { DunningsBulletPoint } from "app/components/Dunnings/DunningsBulletPoint/DunningsBulletPoint";
import {
    DUNNINGS_MODAL_STYLES,
    DUNNINGS_RESTART_TRIAL_EVENT_SUB_SUB_NAME,
    RESTART_TRIAL_DUNNINGS_EVENT_NAME,
} from "app/components/Dunnings/dunningsConstants";
import {
    DunningsModalLeft,
    DunningsModalLeftImage,
    DunningsModalRight,
} from "app/components/Dunnings/DunningsModal/DunningsModalStyled";
import { useDunningsModal } from "app/components/Dunnings/useDunningsModal";
import { afterTrialBannerModalStyles } from "app/common/after-trial/afterTrialBannerModalStyles";
import { AfterTrialPopupContainer } from "app/components/React/AfterTrialPopup/AfterTrialPopupContainer";
import { AssetsService } from "app/services/AssetsService";
import { isSI } from "app/UtilitiesAndConstants/UtilityFunctions/trialUsersUtils";
import { useTranslation } from "pro-features/components/WithTranslation/src/I18n";

export const RestartTrialDunningsModal = () => {
    const { isOpen, onCloseClicked, onDunningsButtonClicked } = useDunningsModal({
        eventName: RESTART_TRIAL_DUNNINGS_EVENT_NAME,
        eventSubSubName: DUNNINGS_RESTART_TRIAL_EVENT_SUB_SUB_NAME,
    });
    const [isStartTrialModalOpen, setIsStartTrialModalOpen] = useState(false);
    const translate = useTranslation();

    const onClick = () => {
        onCloseClicked();
        onDunningsButtonClicked();
        setIsStartTrialModalOpen(true);
    };

    return (
        <>
            <Modal
                isOpen={isOpen}
                customStyles={DUNNINGS_MODAL_STYLES}
                data-automation="restart-trial-dunnings-modal"
            >
                <Flex>
                    <DunningsModalLeft>
                        <Text
                            fontVariant="h3"
                            style={{ padding: "0 40px" }}
                            dangerouslySetInnerHTML={{
                                // eslint-disable-next-line @typescript-eslint/naming-convention
                                __html: purify.sanitize(translate("dunnings.restartTrial.modal.title")),
                            }}
                        />

                        <DunningsModalLeftImage
                            alt={translate("dunnings.restartTrial.modal.image_alt")}
                            src={AssetsService.assetUrl("/images/dunnings/reactivate_modal_image.svg")}
                        />
                    </DunningsModalLeft>

                    <DunningsModalRight>
                        <Stack gap="8px">
                            <Text
                                fontVariant="body2"
                                style={{ width: "100%" }}
                                dangerouslySetInnerHTML={{
                                    // eslint-disable-next-line @typescript-eslint/naming-convention
                                    __html: purify.sanitize(translate("dunnings.restartTrial.modal.text")),
                                }}
                            />

                            <Stack gap="8px">
                                <Text
                                    fontVariant="body2"
                                    dangerouslySetInnerHTML={{
                                        // eslint-disable-next-line @typescript-eslint/naming-convention
                                        __html: purify.sanitize(translate("dunnings.restartTrial.modal.get_access_to")),
                                    }}
                                />

                                <DunningsBulletPoint text={translate("dunnings.restartTrial.modal.bullet_1")} />

                                <DunningsBulletPoint text={translate("dunnings.restartTrial.modal.bullet_2")} />

                                <DunningsBulletPoint text={translate("dunnings.restartTrial.modal.bullet_3")} />
                            </Stack>
                        </Stack>

                        <Stack gap="8px">
                            <Button
                                type="primary"
                                onClick={onClick}
                                style={{ width: "100%" }}
                            >
                                {translate("dunnings.restartTrial.modal.restart_trial")}
                            </Button>

                            <Button
                                type="flat"
                                onClick={onCloseClicked}
                                style={{ width: "100%" }}
                            >
                                {translate("dunnings.restartTrial.modal.close")}
                            </Button>
                        </Stack>
                    </DunningsModalRight>
                </Flex>
            </Modal>

            <Modal
                isOpen={isStartTrialModalOpen}
                onRequestClose={() => setIsStartTrialModalOpen(false)}
                customStyles={afterTrialBannerModalStyles}
                shouldCloseOnOverlayClick={true}
            >
                <AfterTrialPopupContainer
                    setIsOpen={setIsStartTrialModalOpen}
                    isSI={isSI()}
                />
            </Modal>
        </>
    );
};
